/**
 * Stock categorization types for frontend
 * Integrates with premium configuration system for dynamic access control
 */

import { isCategoryPremium } from '../config/premiumConfig';
import {
  CategorySlug,
  CATEGORIES
} from '../config/categoryConfig';

/**
 * Category filter options (imported from central config)
 */
export type StockCategoryFilter = CategorySlug;

/**
 * Stock category data structure
 */
export interface StockCategory {
  symbol: string;
  companyName: string;
  gicsSector?: string;
  gicsSubIndustry?: string;

  // Category flags
  isSpy: boolean;
  isTech: boolean;
  isSoftware: boolean;
  isHardware: boolean;
  isPharma: boolean;
  isFinancial: boolean;
  isConsumer: boolean;
  isMedia: boolean;
  isIndustrial: boolean;
  isEnergy: boolean;
  isRealEstate: boolean;
  isWsb: boolean;
  isForeign: boolean;
  isPopular: boolean;

  // Additional metadata
  marketCap?: number;
  dateAdded?: string;
  headquartersLocation?: string;
  foundedYear?: number;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Category statistics
 */
export interface CategoryStats {
  category: StockCategoryFilter;
  count: number;
  name: string;
}

/**
 * Category display configuration
 */
export interface CategoryDisplayConfig {
  key: StockCategoryFilter;
  label: string;
  description: string;
  color: string;
  icon: string;
  isDefault?: boolean;
  requiresPremium?: boolean; // Access control flag
}

/**
 * Stock category response from API
 */
export interface StockCategoryResponse {
  symbol: string;
  companyName: string;
  categories: StockCategoryFilter[];
  categoryDetails: {
    [key in StockCategoryFilter]?: boolean;
  };
  metadata: {
    gicsSector?: string;
    gicsSubIndustry?: string;
    marketCap?: number;
    dateAdded?: string;
    headquartersLocation?: string;
    foundedYear?: number;
  };
}

/**
 * Stock category list response
 */
export interface StockCategoryListResponse {
  stocks: StockCategory[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  filters: {
    categories: StockCategoryFilter[];
    query?: string;
  };
}

/**
 * Category stats response
 */
export interface CategoryStatsResponse {
  totalStocks: number;
  categoryStats: CategoryStats[];
  lastUpdated?: string;
}

/**
 * Category filter state for UI
 */
export interface CategoryFilterState {
  selectedCategories: StockCategoryFilter[];
  searchQuery: string;
  sortBy: 'symbol' | 'companyName' | 'marketCap';
  sortOrder: 'asc' | 'desc';
  page: number;
  limit: number;
}

/**
 * Base category display configuration (generated from central config)
 */
const BASE_CATEGORY_CONFIGS: Record<StockCategoryFilter, Omit<CategoryDisplayConfig, 'requiresPremium'>> =
  CATEGORIES.reduce((acc, category) => {
    acc[category.slug as StockCategoryFilter] = {
      key: category.slug as StockCategoryFilter,
      label: category.displayName,
      description: category.description,
      color: category.color,
      icon: category.icon,
      isDefault: category.isDefault
    };
    return acc;
  }, {} as Record<StockCategoryFilter, Omit<CategoryDisplayConfig, 'requiresPremium'>>);

/**
 * Get category display configuration with dynamic premium flags
 */
export function getCategoryConfigs(): Record<StockCategoryFilter, CategoryDisplayConfig> {
  const configs: Record<StockCategoryFilter, CategoryDisplayConfig> = {} as any;

  for (const [key, baseConfig] of Object.entries(BASE_CATEGORY_CONFIGS)) {
    configs[key as StockCategoryFilter] = {
      ...baseConfig,
      requiresPremium: isCategoryPremium(key as StockCategoryFilter)
    } as CategoryDisplayConfig;
  }

  return configs;
}

/**
 * @deprecated Use getCategoryConfigs() for dynamic configuration
 * Legacy constant for backward compatibility
 * Category display configuration lookup (static, may not reflect current premium settings)
 */
export const CATEGORY_CONFIGS: Record<StockCategoryFilter, CategoryDisplayConfig> = CATEGORIES.reduce(
  (acc, category) => {
    acc[category.slug as StockCategoryFilter] = {
      key: category.slug as StockCategoryFilter,
      label: category.displayName,
      description: category.description,
      color: category.color,
      icon: category.icon,
      isDefault: category.isDefault,
      requiresPremium: isCategoryPremium(category.slug as StockCategoryFilter)
    };
    return acc;
  },
  {} as Record<StockCategoryFilter, CategoryDisplayConfig>
);

/**
 * Helper functions for category management
 */
export const CategoryHelpers = {
  /**
   * Get display config for a category (with dynamic premium flags)
   */
  getConfig: (category: StockCategoryFilter): CategoryDisplayConfig => {
    const configs = getCategoryConfigs();
    return configs[category];
  },

  /**
   * Get all categories for display (with dynamic premium flags)
   */
  getAllCategories: (): CategoryDisplayConfig[] => {
    const configs = getCategoryConfigs();
    return Object.values(configs);
  },

  /**
   * Get default categories (usually just SPY)
   */
  getDefaultCategories: (): StockCategoryFilter[] => {
    const configs = getCategoryConfigs();
    return Object.values(configs)
      .filter(config => config.isDefault)
      .map(config => config.key);
  },

  /**
   * Check if a category is valid
   */
  isValidCategory: (category: string): category is StockCategoryFilter => {
    const configs = getCategoryConfigs();
    return category in configs;
  },

  /**
   * Format market cap for display
   */
  formatMarketCap: (marketCap?: number): string => {
    if (!marketCap) return 'N/A';

    if (marketCap >= 1e12) {
      return `$${(marketCap / 1e12).toFixed(1)}T`;
    } else if (marketCap >= 1e9) {
      return `$${(marketCap / 1e9).toFixed(1)}B`;
    } else if (marketCap >= 1e6) {
      return `$${(marketCap / 1e6).toFixed(1)}M`;
    } else {
      return `$${marketCap.toLocaleString()}`;
    }
  },

  /**
   * Get active categories from a stock
   * Note: Only returns live categories (media and real_estate are retired)
   */
  getActiveCategories: (stock: StockCategory): StockCategoryFilter[] => {
    const categories: StockCategoryFilter[] = [];

    if (stock.isPopular) categories.push('popular');
    if (stock.isTech) categories.push('tech');
    if (stock.isSoftware) categories.push('software');
    if (stock.isHardware) categories.push('hardware');
    if (stock.isPharma) categories.push('pharma');
    if (stock.isFinancial) categories.push('financial');
    if (stock.isConsumer) categories.push('consumer');
    if (stock.isIndustrial) categories.push('industrial');
    if (stock.isEnergy) categories.push('energy');
    if (stock.isWsb) categories.push('wallstreetbets');
    if (stock.isForeign) categories.push('foreign');

    return categories;
  },

  /**
   * Check if stock belongs to category
   */
  stockBelongsToCategory: (stock: StockCategory, category: StockCategoryFilter): boolean => {
    switch (category) {
      case 'popular': return stock.isPopular;
      case 'tech': return stock.isTech;
      case 'software': return stock.isSoftware;
      case 'hardware': return stock.isHardware;
      case 'pharma': return stock.isPharma;
      case 'financial': return stock.isFinancial;
      case 'consumer': return stock.isConsumer;
      case 'industrial': return stock.isIndustrial;
      case 'energy': return stock.isEnergy;
      case 'wallstreetbets': return stock.isWsb;
      case 'foreign': return stock.isForeign;
      default: return false;
    }
  },

  /**
   * Sort stocks by various criteria
   */
  sortStocks: (
    stocks: StockCategory[],
    sortBy: 'symbol' | 'companyName' | 'marketCap',
    order: 'asc' | 'desc'
  ): StockCategory[] => {
    return [...stocks].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'symbol':
          comparison = a.symbol.localeCompare(b.symbol);
          break;
        case 'companyName':
          comparison = a.companyName.localeCompare(b.companyName);
          break;
        case 'marketCap':
          comparison = (a.marketCap || 0) - (b.marketCap || 0);
          break;
      }

      return order === 'asc' ? comparison : -comparison;
    });
  },

  /**
   * Filter stocks by search query
   */
  filterStocksByQuery: (stocks: StockCategory[], query: string): StockCategory[] => {
    if (!query.trim()) return stocks;

    const lowerQuery = query.toLowerCase();
    return stocks.filter(stock =>
      stock.symbol.toLowerCase().includes(lowerQuery) ||
      stock.companyName.toLowerCase().includes(lowerQuery)
    );
  }
};

/**
 * Error types for category operations
 */
export interface CategoryError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

/**
 * Loading states for category operations
 */
export interface CategoryLoadingState {
  isLoading: boolean;
  isRefreshing: boolean;
  error: CategoryError | null;
  lastUpdated?: Date;
}

/**
 * API request options for category endpoints
 */
export interface CategoryApiOptions {
  categories?: StockCategoryFilter[];
  query?: string;
  limit?: number;
  offset?: number;
  includeDetails?: boolean;
}
